<template>
  <div class="form__wrapper">
    <div v-if="!isCareerPage" class="form__header">
      <h3 class="form__title">{{ $t("form.title") }}</h3>
      <p class="form__text">{{ $t("form.text") }}</p>
    </div>

    <div v-else class="form__header">
      <h3 class="form__title">{{ $t("form.careerTitle") }}</h3>
      <p class="form__text">{{ $t("form.careerText") }}</p>
    </div>

    <div class="form__content form__content--medium">
      <form @submit.prevent="submitForm" class="form">
        <div class="form__row">
          <input class="input__field" :class="{ 'input-error': fieldErrors.name }" type="text"
                 :placeholder="$t('form.placeholders.name')" v-model="formData.name" maxlength="60"/>
          <input v-model="formData.phone_number"
                 v-mask="'+7 (###) ###-##-##'"
                 :class="{ 'input-error': fieldErrors.phone_number }" class="input__field" type="tel"
                 :placeholder="$t('form.placeholders.phone')"/>
        </div>

        <div v-if="!isCareerPage" class="form__row">
          <input class="input__field" :class="{ 'input-error': fieldErrors.email }" type="text"
                 :placeholder="$t('form.placeholders.email')" maxlength="255" v-model="formData.email"/>
          <div class="dropdown" @click="toggleDropdown">
            <div class="input__field input__field--dropdown" :class="{
                            'input__field--dropdown-selected': selectedServiceTitle[locale],
                            'input-error': fieldErrors.service_id,
                            'input-error--text-error':
                                fieldErrors.service_id &&
                                !selectedServiceTitle[locale],
                        }">
              <div>{{ selectedServiceTitle[locale] || $t("form.serviceType") }}</div>
            </div>
            <div v-if="isDropdownOpen" class="dropdown-list">
              <div v-for="(service, index) in services"
                   :key="index"
                   class="dropdown-item"
                   @click="selectService(service)"
                   :class="{ 'dropdown-item--selected': isSelectedService(service) }"
              >
                {{ service.title[0][locale] }}
                <IconsCheckMark v-if="isSelectedService(service)"/>
              </div>
            </div>
          </div>
          <IconsArrowDown class="dropdown__arrow"/>
        </div>

        <div v-else class="form__row">
          <input class="input__field" :class="{ 'input-error': fieldErrors.position }" type="text"
                 :placeholder="$t('form.placeholders.position')" v-model="formData.position" maxlength="60"/>
          <input v-model="formData.link" :class="{ 'input-error': fieldErrors.link }" class="input__field"
                 type="text" :placeholder="$t('form.placeholders.link')"/>
        </div>

        <div class="form__row checkbox">
          <input class="checkbox__field" type="checkbox" id="consent"
                 v-model="formData.processing_of_personal_data"/>
          <label class="checkbox__text" for="consent">
            <span class="checkbox__custom" :class="{ 'input-error': fieldErrors.processing_of_personal_data }">
              <IconsPolicyCheckMark v-if="formData.processing_of_personal_data"/>
            </span>
            {{ $t("form.consentText") }}
            <NuxtLink
              v-if="getPolicyUrl"
              class="checkbox__policy"
              target="_blank"
              :to="useRuntimeConfig().public.storage_host + getPolicyUrl"
            >
              {{ $t("form.consentTextLink") }}
            </NuxtLink>
          </label>
        </div>

        <div class="form__row form__button--medium">
          <button class="form__button button button--primary" :class="{
                        'button--primary--disabled': isButtonDisabled,
                    }" type="submit">
            <div v-if="!isCareerPage">
              {{ $t("form.buttonText") }}
            </div>
            <div v-else>
              {{ $t("form.buttonCareer") }}
            </div>
          </button>
          <div v-if="errors.length" class="error">
            <p>{{ errors[0] }}</p>
          </div>
        </div>
        <div class="form__content form__content--small">
          <button class="form__button button button--primary">
            <div v-if="!isCareerPage">
              {{ $t("form.buttonMobile") }}
            </div>
            <div v-else>
              {{ $t("form.buttonCareer") }}
            </div>
          </button>
          <div v-if="errors.length" class="error error--small">
            <p>{{ errors[0] }}</p>
          </div>
        </div>
        <div v-if="isCaptchaVisible" class="form__recaptcha">
          <RecaptchaComponent style="width: 293px" @get-key="getKey"/>
        </div>
      </form>
    </div>
    <FormMessage v-if="isSuccessMessageVisible" @close="this.isSuccessMessageVisible = false"/>
  </div>
</template>

<script>
import { useDocumentsStore } from "~/stores/documents/documents";

const apiEndpoint = "api/services";
const apiEndpointForm = "api/request-forms";
const apiEndpointResume = "api/resume";

export default {
  name: "FormSection",
  data() {
    return {
      formData: {
        name: "",
        phone_number: "",
        email: "",
        service_id: "",
        city_id: 1,
        position: "",
        link: "",
        processing_of_personal_data: false,
        "g-recaptcha-response": "",
      },
      errors: [],
      isClient: false,
      isDropdownOpen: false,
      isButtonDisabled: false,
      isSuccessMessageVisible: false,
      isCaptchaVisible: false,
      selectedServiceTitle: {},
      services: [],
      fieldErrors: {
        name: false,
        phone_number: false,
        email: false,
        service_id: false,
        processing_of_personal_data: false,
        position: "",
        link: "",
      },
      runtimeConfig: useRuntimeConfig(),
      isLoaded: false
    };
  },
  watch: {
    formData: {
      handler(form) {
        if (
          (form.name &&
            form.phone_number &&
            form.service_id &&
            form.processing_of_personal_data) ||
          (form.name &&
            form.phone_number &&
            form.link &&
            form.processing_of_personal_data)
        ) {
          this.isCaptchaVisible = true;
        }
      },
      deep: true,
      immediate: true,
    },
    services: {
      handler() {
        const queryId = this.$route.query.id;
        if (queryId) {
          const serviceTypeBypage = this.services.find((item) => String(item.id) === queryId);
          this.selectService(serviceTypeBypage);
          if (this.isLoaded) {
            this.toggleDropdown();
            this.isDropdownOpen = false;
          }
        }
      },
      deep: true,
      immediate: true,
    },
    '$route.query.id': {
      handler(queryId) {
        if (queryId) {
          const serviceTypeBypage = this.services.find((item) => String(item.id) === queryId);
          this.toggleDropdown();
          this.selectService(serviceTypeBypage);
        }
      }
    },
  },

  mounted() {
    this.isClient = typeof window !== "undefined";
    this.isLoaded = true;
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  async created() {
    try {
      const response = await useFetch(
        useRuntimeConfig().public.api_host + apiEndpoint
      );
      this.services = response.data;
    } catch (err) {
      this.error = err;
    }
  },

  setup() {
    const {locale} = useI18n();
    return {
      locale,
    };
  },
  computed: {
    getPolicyUrl() {
      return useDocumentsStore().getPolicyDocumentUrl();
    },
    isCareerPage() {
      return this.$route.fullPath === "/career";
    },
  },
  methods: {
    getKey(key) {
      this.formData["g-recaptcha-response"] = key;
    },
    async submitForm() {
      const cityCookie = useCookie('active-city');
      this.formData.city_id = Number(cityCookie.value) || 0;

      if (!this.validateForm()) {
        return;
      }
      if (!this.isCareerPage) {
        try {
          await useFetch(
            useRuntimeConfig().public.api_host + apiEndpointForm,
            {
              method: "POST",
              headers: {Accept: "application/json"},
              body: this.formData,
            }
          );
          this.isButtonDisabled = true;
          this.resetForm();
        } catch (err) {
          console.error("Error submitting form:", err);
        }
      } else {
        try {
          await useFetch(useRuntimeConfig().public.api_host + apiEndpointResume,
            {
              method: "POST",
              headers: {Accept: "application/json"},
              body: this.formData,
            }
          );
          this.isButtonDisabled = true;
          this.resetForm();
        } catch (err) {
          console.error("Error submitting form:", err);
        }
      }
    },
    validateForm() {
      this.errors = [];
      this.fieldErrors = {
        name: false,
        phone: false,
        email: false,
        service_id: false,
        processing_of_personal_data: false,
        position: false,
        link: false,
      };

      if (!this.formData.name) {
        this.errors.push(this.$t("form.errors.nameRequired"));
        this.fieldErrors.name = true;
      }

      const namePattern = /^[a-zA-Zа-яА-ЯёЁ\s\-]+$/;
      if (!namePattern.test(this.formData.name)) {
        this.errors.push(this.$t("form.errors.nameInvalid"));
        this.fieldErrors.name = true;
      }

      if (!this.formData.phone_number) {
        this.errors.push(this.$t("form.errors.phoneRequired"));
        this.fieldErrors.phone_number = true;
      }

      const phonePattern = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/;
      if (!phonePattern.test(this.formData.phone_number)) {
        this.errors.push(this.$t("form.errors.phoneInvalid"));
        this.fieldErrors.phone_number = true;
      }

      if (!this.isCareerPage) {
        const emailPattern =
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (
          this.formData.email &&
          !emailPattern.test(this.formData.email)
        ) {
          this.errors.push(this.$t("form.errors.emailInvalid"));
          this.fieldErrors.email = true;
        }

        if (!this.formData.service_id) {
          this.errors.push(this.$t("form.errors.serviceInvalid"));
          this.fieldErrors.service_id = true;
        }
      } else {
        const linkPattern =
          /^(https?:\/\/)?([a-z\d-]+\.)+[a-z]{2,6}\/?/i;
        if (
          (this.formData.link &&
            !linkPattern.test(this.formData.link)) ||
          !this.formData.link
        ) {
          this.errors.push(this.$t("form.errors.linkInvalid"));
          this.fieldErrors.link = true;
        }
      }

      if (!this.formData.processing_of_personal_data) {
        this.errors.push(this.$t("form.errors.personalDataRequired"));
        this.fieldErrors.processing_of_personal_data = true;
      }

      if (!this.formData["g-recaptcha-response"]) {
        this.errors.push(this.$t("form.errors.captcha"));
        this.fieldErrors.processing_of_personal_data = true;
        return false;
      }

      return this.errors.length === 0;
    },
    resetForm() {
      this.formData = {
        name: "",
        phone_number: "",
        email: "",
        city_id: "",
        service_id: "",
        processing_of_personal_data: false,
        "g-recaptcha-response": "",
        position: "",
        link: "",
      };
      this.errors = [];
      this.isCaptchaVisible = false;

      const cityCookie = useCookie('active-city');
      let cityId = Number(cityCookie.value) || 0;
      cityId
        ? (this.formData.city_id = cityId)
        : (this.formData.city_id = 1);

      (this.selectedServiceTitle = {}), (this.errors = []);

      this.isSuccessMessageVisible = true;
      setTimeout(() => {
        this.isButtonDisabled = false;
        this.isSuccessMessageVisible = false;
      }, 5000);
    },
    toggleDropdown() {
      if (!this.isDropdownOpen) {
        this.isDropdowIconVisible = false;
      }
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    selectService(service) {
      if (service && service.id) {
        this.formData.service_id = service.id;
        this.selectedServiceTitle = service.title[0];
        this.isDropdownOpen = false;
      } else {
        this.formData.service_id = '';
        this.selectedServiceTitle = {};
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isDropdownOpen = false;
      }
    },
    isSelectedService(service) {
      return this.formData.service_id === service.id;
    },
  },
};
</script>

<style lang="scss" scoped>
.form__wrapper {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.6) url("/public/form-background.png");
  min-height: 209px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  padding: 40px 16px;

  @media (min-width: $screen-m) {
    padding: 50px 40px;
  }

  @media (min-width: $screen-l) {
    flex-direction: row;
    padding: 60px;
    gap: 57px;
  }

  @media (min-width: $screen-l) {
    gap: 47px;
  }
}

.form__header {
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: $screen-m) {
    align-items: flex-start;
  }

  @media (min-width: $screen-l) {
    min-width: 371px;
  }

  @media (min-width: $screen-xl) {
    min-width: 445px;
  }
}

.form__content {
  display: block;
  width: 100%;

  &--small {
    @media (min-width: $screen-m) {
      display: none;
    }
  }
}

.form__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: $screen-m - 1) {
    flex-direction: column;
  }
}

.checkbox {
  gap: 12px;
}

.checkbox__field {
  display: none;
}

.checkbox__custom {
  display: flex;
  text-align: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid #fff;
  background-color: #fff;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.checkbox__text {
  position: relative;
  cursor: pointer;
  padding-left: 32px;
  font-weight: 500;
  font-size: 14px;
  line-height: 146%;
  color: #fff;

  @media (max-width: $screen-m - 1) {
    margin-right: auto;
  }
}

.checkbox__policy {
  color: #ed1c24;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    height: 1px;
    width: 100%;
    background-color: #ed1c24;
  }
}

.form__button {
  &--medium {
    @media (max-width: $screen-m - 1) {
      display: none;
    }
  }
}

.form__title {
  font-weight: 500;
  font-size: 18px;
  line-height: 140%;
  color: #fff;

  @media (min-width: $screen-m) {
    font-size: 32px;
    line-height: 124%;
  }

  @media (min-width: $screen-xl) {
    font-size: 40px;
  }
}

.form__text {
  font-weight: 400;
  font-size: 14px;
  line-height: 146%;
  color: #fff;
  margin: 16px 0 24px;

  @media (min-width: $screen-l) {
    font-size: 16px;
    line-height: 152%;
    margin: 24px 0 0;
  }
}

.form__recaptcha {
  margin-top: 24px;
}

.input__field {
  width: 50%;
  height: 56px;
  border-radius: 8px;
  padding: 16px 24px;
  background: #fff;
  border: 2px solid #ccc;
  font-weight: 400;
  font-size: 16px;
  line-height: 152%;
  color: #282828;

  &--promo {
    width: calc(50% - 10px);
  }

  &--dropdown {
    color: #7a7a7a;
    width: auto;
  }

  &--dropdown-selected {
    color: #282828;
  }

  &--selected-text {
    max-width: 90%;
    text-wrap: nowrap;
    overflow: hidden;
  }

  @media (max-width: $screen-m - 1) {
    width: 100%;
  }
}

.error {
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #ed1c24;

  &--small {
    margin-top: 10px;
  }

  @media (min-width: $screen-m) {
    font-size: 14px;
    line-height: 146%;
    max-width: 250px;
  }
}

.input-error {
  border: 2px solid #ed1c24;

  &::placeholder {
    color: #ed1c24;
  }

  &--text-error {
    > div {
      color: #ed1c24;
    }
  }
}

.textarea__field {
  width: 100%;
  height: 104px;
  resize: none;
  outline: none;
}

.dropdown {
  position: relative;
  width: 50%;

  @media (max-width: $screen-m - 1) {
    width: 100%;
  }
}

.dropdown__arrow {
  position: absolute;
  right: 30px;
  bottom: 24px;
  pointer-events: none;
  color: #7a7a7a;
}

.dropdown-list {
  position: absolute;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 8px;
  border: none;
  background-color: white;
  z-index: 10;
  top: 61px;
}

.dropdown-item {
  display: flex;
  justify-content: space-between;
  padding: 24px 18px;
  color: #282828;
  cursor: pointer;

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:hover {
    background-color: #ffe7e7;
  }

  &--selected {
    color: #ed1c24;
  }
}

.dropdown-icon {
  display: flex;
}
</style>
